import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { uploadCompanyLogo } from "@/lib/fileAccess";
import AdminLayout from "@/components/AdminLayout";
import AuthenticatedImage from "@/components/AuthenticatedImage";
import { Building, Upload, Save, Loader2, ArrowLeft } from "lucide-react";
import { INDIA_STATES } from "@/lib/indiaStates";

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

const Field = ({ label, children, full, required }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
      {label}{required ? <span className="text-rose-600"> *</span> : null}
    </label>
    {children}
  </div>
);

export default function Company() {
  const { companyId: routeCompanyId } = useParams();
  const companyId = routeCompanyId || "company";
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    setForm(null);
    api
      .get(`/companies/${companyId}`)
      .then((r) => setForm(r.data))
      .catch(() => {
        api.get("/company", { params: { company_id: companyId } }).then((r) => setForm(r.data));
      });
  }, [companyId]);

  if (!form) {
    return (
      <AdminLayout title="Company Profile" breadcrumbs={["Home", "Organization", "Companies", companyId]}>
        <div className="text-slate-500">Loading…</div>
      </AdminLayout>
    );
  }

  const set = (k, v) => setForm({ ...form, [k]: v });

  const handleLogo = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setMsg("Logo is too large (max 5 MB)");
      return;
    }
    setLogoUploading(true);
    setMsg("");
    try {
      const result = await uploadCompanyLogo(companyId, file);
      setForm((f) => ({
        ...f,
        logo_url: result.logo_url,
        logo_storage_path: result.logo_storage_path,
      }));
      setMsg("Logo uploaded");
      setTimeout(() => setMsg(""), 3000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Logo upload failed");
    } finally {
      setLogoUploading(false);
    }
  };

  const onSave = async () => {
    if (!(form.state || "").trim()) {
      setMsg("Company State is required (Professional Tax / legal entity).");
      return;
    }
    setSaving(true);
    setMsg("");
    try {
      const payload = { ...form, id: companyId };
      delete payload.logo_storage_path;
      await api.put("/company", payload);
      const refreshed = await api.get(`/companies/${companyId}`);
      setForm(refreshed.data);
      setMsg("Company profile saved successfully");
      setTimeout(() => setMsg(""), 3000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout
      title={form.name || "Company Profile"}
      breadcrumbs={["Home", "Organization", "Companies", form.name || companyId]}
    >
      <div className="mb-4">
        <Link
          to="/companies"
          className="inline-flex items-center gap-1.5 text-sm text-[#226DB4] hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> All Companies
        </Link>
      </div>

      <div className="bg-white border border-slate-200 rounded shadow-sm">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Building className="h-5 w-5 text-[#226DB4]" /> Company Details
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              ID: <code className="bg-slate-100 px-1 rounded">{companyId}</code>
            </p>
          </div>
          {msg && (
            <span data-testid="company-save-msg" className="text-sm text-emerald-600">
              {msg}
            </span>
          )}
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-center gap-5">
            <div className="h-26 w-40 rounded border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
              {form.logo_url ? (
                <AuthenticatedImage
                  src={form.logo_url}
                  alt="logo"
                  className="h-full w-full object-contain"
                  fallback={<Building className="h-10 w-10 text-slate-300" />}
                />
              ) : (
                <Building className="h-10 w-10 text-slate-300" />
              )}
            </div>
            <label className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 rounded cursor-pointer hover:bg-slate-50">
              {logoUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Upload Logo
              <input
                data-testid="company-logo-input"
                type="file"
                accept="image/*"
                className="hidden"
                disabled={logoUploading}
                onChange={(e) => {
                  handleLogo(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </label>
            <p className="text-xs text-slate-500 max-w-xs">
              Stored on disk under{" "}
              <code className="text-[11px]">companies/{companyId}/logo</code>
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Display Name">
              <input
                data-testid="company-name-input"
                className={inputCls}
                value={form.name || ""}
                onChange={(e) => set("name", e.target.value)}
              />
            </Field>
            <Field label="Legal Name">
              <input
                className={inputCls}
                value={form.legal_name || ""}
                onChange={(e) => set("legal_name", e.target.value)}
              />
            </Field>
            <Field label="Address Line 1" full>
              <input
                className={inputCls}
                value={form.address_line1 || ""}
                onChange={(e) => set("address_line1", e.target.value)}
              />
            </Field>
            <Field label="Address Line 2" full>
              <input
                className={inputCls}
                value={form.address_line2 || ""}
                onChange={(e) => set("address_line2", e.target.value)}
              />
            </Field>
            <Field label="City">
              <input
                className={inputCls}
                value={form.city || ""}
                onChange={(e) => set("city", e.target.value)}
              />
            </Field>
            <Field label="State" required>
              <select
                className={inputCls}
                value={form.state || ""}
                onChange={(e) => set("state", e.target.value)}
                required
              >
                <option value="">Select state…</option>
                {INDIA_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Country">
              <input
                className={inputCls}
                value={form.country || ""}
                onChange={(e) => set("country", e.target.value)}
              />
            </Field>
            <Field label="Pincode">
              <input
                className={inputCls}
                value={form.pincode || ""}
                onChange={(e) => set("pincode", e.target.value)}
              />
            </Field>

            <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-1">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                Statutory & Tax
              </div>
            </div>
            <Field label="GSTIN">
              <input
                data-testid="company-gstin-input"
                className={inputCls}
                value={form.gstin || ""}
                onChange={(e) => set("gstin", e.target.value.toUpperCase())}
                placeholder="29ABCDE1234F1Z5"
              />
            </Field>
            <Field label="PAN">
              <input
                data-testid="company-pan-input"
                className={inputCls}
                value={form.pan || ""}
                onChange={(e) => set("pan", e.target.value.toUpperCase())}
              />
            </Field>
            <Field label="CIN">
              <input
                className={inputCls}
                value={form.cin || ""}
                onChange={(e) => set("cin", e.target.value.toUpperCase())}
              />
            </Field>
            <Field label="TAN">
              <input
                className={inputCls}
                value={form.tan || ""}
                onChange={(e) => set("tan", e.target.value.toUpperCase())}
              />
            </Field>

            <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-1">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                Contact
              </div>
            </div>
            <Field label="Email">
              <input
                className={inputCls}
                value={form.email || ""}
                onChange={(e) => set("email", e.target.value)}
              />
            </Field>
            <Field label="Phone">
              <input
                className={inputCls}
                value={form.phone || ""}
                onChange={(e) => set("phone", e.target.value)}
              />
            </Field>
            <Field label="Website" full>
              <input
                className={inputCls}
                value={form.website || ""}
                onChange={(e) => set("website", e.target.value)}
              />
            </Field>

            <Field label="Financial Year Start Month">
              <select
                className={inputCls}
                value={form.financial_year_start_month || 4}
                onChange={(e) =>
                  set("financial_year_start_month", parseInt(e.target.value, 10))
                }
              >
                {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>
            </Field>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end">
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="company-save-btn"
            className="inline-flex items-center gap-2 px-5 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium disabled:opacity-70"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Changes
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
